/**
 * Utility functions for handling notification navigation
 * Centralized logic to avoid duplication between NotificationBell and Notifications page
 */

/**
 * Get the deep link URL from related objects if available
 * @param {Object} notification - The notification object
 * @returns {string|null} The deep link URL or null if not applicable
 */
const ALLOWED_DEEP_LINK_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  'dev.prontohousing.io',
  'stage.prontohousing.io',
  'prontohousing.io',
]);

const isAllowedDeepLinkOrigin = (url) => {
  if (!url) {
    return false;
  }
  const host = url.hostname;
  if (ALLOWED_DEEP_LINK_HOSTS.has(host)) {
    return true;
  }
  if (host.endsWith('.prontohousing.io')) {
    return true;
  }
  return false;
};

/**
 * Normalize a notification URL so navigation stays on the current environment.
 * For allowed Pronto hosts (dev/stage/production), returns a relative path so
 * the user is not sent to a different environment.
 * @param {string} urlString - Full URL or relative path from the backend
 * @returns {string|null} Relative path for same-environment navigation, or null if not allowed
 */
export const toCurrentEnvironmentUrl = (urlString) => {
  if (typeof urlString !== 'string' || !urlString.trim()) {
    return null;
  }
  const trimmed = urlString.trim();
  if (trimmed.startsWith('/')) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (!isAllowedDeepLinkOrigin(url)) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    try {
      const url = new URL(trimmed, window.location.origin);
      if (!isAllowedDeepLinkOrigin(url)) {
        return null;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
};

export const getDeepLinkUrl = (notification) => {
  const deepLink = notification?.related_objects?.deep_link;
  if (!deepLink) {
    return null;
  }

  try {
    const url = new URL(deepLink, window.location.origin);
    if (!isAllowedDeepLinkOrigin(url)) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

/**
 * Get the URL for property-level communication notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getPropertyCommunicationUrl = (notification) => {
  if (!notification.is_property_notification) {
    return null;
  }

  const communicationType = notification.notification_type;
  if (
    communicationType === 'new_email' ||
    communicationType === 'new_sms' ||
    communicationType === 'new_fax'
  ) {
    const propertySlug =
      notification.property?.slug ||
      notification.property?.name?.toLowerCase().replace(/\s+/g, '-') ||
      (notification.properties && notification.properties[0]?.slug) ||
      (notification.properties &&
        notification.properties[0]?.name?.toLowerCase().replace(/\s+/g, '-'));

    if (propertySlug) {
      const filter =
        communicationType === 'new_email'
          ? 'email'
          : communicationType === 'new_sms'
          ? 'sms'
          : 'fax';
      return `/workbench/${propertySlug}/?t1=communications&filter=${filter}`;
    }
  }

  return null;
};

/**
 * Get the URL for certification-level communication notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getCertificationCommunicationUrl = (notification) => {
  if (!notification.is_single_notification) {
    return null;
  }

  const communicationType = notification.notification_type;
  if (
    communicationType === 'new_email' ||
    communicationType === 'new_sms' ||
    communicationType === 'new_fax'
  ) {
    // Try new data structure first
    if (notification.certifications && notification.certifications[0]?.id) {
      const cert = notification.certifications[0];
      const propertySlug =
        cert.property?.slug ||
        cert.property?.name?.toLowerCase().replace(/\s+/g, '-');
      if (propertySlug && cert.id) {
        const filter =
          communicationType === 'new_email'
            ? 'email'
            : communicationType === 'new_sms'
            ? 'sms'
            : 'fax';
        return `/workbench/${propertySlug}/${cert.id}/communications?filter=${filter}`;
      }
    }

    // Fallback to legacy data structure
    if (notification.certification?.id) {
      const propertySlug =
        notification.certification.property?.slug ||
        notification.certification.property?.name
          ?.toLowerCase()
          .replace(/\s+/g, '-');
      if (propertySlug) {
        const filter =
          communicationType === 'new_email'
            ? 'email'
            : communicationType === 'new_sms'
            ? 'sms'
            : 'fax';
        return `/workbench/${propertySlug}/${notification.certification.id}/communications?filter=${filter}`;
      }
    }
  }

  return null;
};

/**
 * Get the URL for document upload notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getDocumentUploadUrl = (notification) => {
  if (
    notification.notification_type !== 'new_documents' ||
    !notification.is_single_notification
  ) {
    return null;
  }

  const relatedObjects = notification.related_objects || {};
  const surveyFileId = relatedObjects.survey_file_id;
  const qitId = relatedObjects.qit_id;
  const surveyId = relatedObjects.survey_id;
  const answerGroupId = relatedObjects.answer_group_id;

  // Try new data structure first
  if (notification.certifications && notification.certifications[0]?.id) {
    const cert = notification.certifications[0];
    const propertySlug =
      cert.property?.slug ||
      cert.property?.name?.toLowerCase().replace(/\s+/g, '-');
    if (propertySlug && cert.id) {
      if (surveyFileId && qitId && surveyId && answerGroupId) {
        return `/workbench/${propertySlug}/${cert.id}/summary?ws_doc=1&qit_id=${qitId}&survey_id=${surveyId}&answer_group_id=${answerGroupId}&file_id=${surveyFileId}`;
      }
      return `/workbench/${propertySlug}/${cert.id}/`;
    }
  }

  // Fallback to legacy data structure
  if (notification.certification?.id) {
    const propertySlug =
      notification.certification.property?.slug ||
      notification.certification.property?.name
        ?.toLowerCase()
        .replace(/\s+/g, '-');
    if (propertySlug) {
      if (surveyFileId && qitId && surveyId && answerGroupId) {
        return `/workbench/${propertySlug}/${notification.certification.id}/summary?ws_doc=1&qit_id=${qitId}&survey_id=${surveyId}&answer_group_id=${answerGroupId}&file_id=${surveyFileId}`;
      }
      return `/workbench/${propertySlug}/${notification.certification.id}/`;
    }
  }

  return null;
};

/**
 * Get the URL for file owner notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getFileOwnerUrl = (notification) => {
  if (
    notification.notification_type !== 'new_file_owner' ||
    !notification.is_single_notification
  ) {
    return null;
  }

  // Try new data structure first
  if (notification.certifications && notification.certifications[0]?.id) {
    const cert = notification.certifications[0];
    const propertySlug =
      cert.property?.slug ||
      cert.property?.name?.toLowerCase().replace(/\s+/g, '-');
    if (propertySlug && cert.id) {
      return `/workbench/${propertySlug}/${cert.id}/`;
    }
  }

  // Fallback to legacy data structure
  if (notification.certification?.id) {
    const propertySlug =
      notification.certification.property?.slug ||
      notification.certification.property?.name
        ?.toLowerCase()
        .replace(/\s+/g, '-');
    if (propertySlug) {
      return `/workbench/${propertySlug}/${notification.certification.id}/`;
    }
  }

  return null;
};

/**
 * Get the URL for status note notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getStatusNoteUrl = (notification) => {
  if (notification.notification_type !== 'new_status_note') {
    return null;
  }

  // Try new data structure first
  if (notification.certifications && notification.certifications[0]?.id) {
    const cert = notification.certifications[0];
    const propertySlug =
      cert.property?.slug ||
      cert.property?.name?.toLowerCase().replace(/\s+/g, '-');
    if (propertySlug && cert.id) {
      return `/workbench/${propertySlug}/${cert.id}/`;
    }
  }

  // Fallback to legacy data structure
  if (notification.certification?.id) {
    const propertySlug =
      notification.certification.property?.slug ||
      notification.certification.property?.name
        ?.toLowerCase()
        .replace(/\s+/g, '-');
    if (propertySlug) {
      return `/workbench/${propertySlug}/${notification.certification.id}/`;
    }
  }

  return null;
};

/**
 * Get the URL for renter notifications (questionnaire, esign, and sign-in reminders)
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getRenterNotificationUrl = (notification) => {
  // Check if this is a renter notification
  if (
    notification.notification_source !== 'renter' &&
    notification.source !== 'renter'
  ) {
    return null;
  }

  // Renter questionnaire, esign, and sign-in reminder notifications should go to /user/
  if (
    notification.notification_type ===
      'renter_questionnaire_required_reminder' ||
    notification.notification_type === 'renter_esign_documents_reminder' ||
    notification.notification_type === 'renter_sign_in_reminder' ||
    notification.notification_type === 'renter_household_composition_reminder'
  ) {
    return '/user/';
  }

  return null;
};

/**
 * Get the URL for legacy single certification notifications
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if not applicable
 */
export const getLegacyCertificationUrl = (notification) => {
  // Try new data structure first
  if (notification.certifications && notification.certifications[0]?.id) {
    const cert = notification.certifications[0];
    const propertySlug =
      cert.property?.slug ||
      cert.property?.name?.toLowerCase().replace(/\s+/g, '-');
    if (propertySlug && cert.id) {
      return `/workbench/${propertySlug}/${cert.id}/`;
    }
  }

  // Fallback to legacy data structure
  if (notification.certification?.id) {
    const propertySlug =
      notification.certification.property?.slug ||
      notification.certification.property?.name
        ?.toLowerCase()
        .replace(/\s+/g, '-');
    if (propertySlug) {
      return `/workbench/${propertySlug}/${notification.certification.id}/`;
    }
  }

  return null;
};

/**
 * Main function to determine the navigation URL for a notification
 * @param {Object} notification - The notification object
 * @returns {string|null} The navigation URL or null if no navigation is available
 */
export const getNotificationNavigationUrl = (notification) => {
  // Priority order: renter notifications > property communications > certification communications > documents > file owner > status note > deep link > legacy > URL

  // 1. Renter notifications (questionnaire, esign, and sign-in reminders)
  const renterUrl = getRenterNotificationUrl(notification);
  if (renterUrl) {
    return renterUrl;
  }

  // 2. Property-level communications
  const propertyUrl = getPropertyCommunicationUrl(notification);
  if (propertyUrl) {
    return propertyUrl;
  }

  // 3. Certification-level communications
  const certificationUrl = getCertificationCommunicationUrl(notification);
  if (certificationUrl) {
    return certificationUrl;
  }

  // 4. Document upload notifications
  const documentUrl = getDocumentUploadUrl(notification);
  if (documentUrl) {
    return documentUrl;
  }

  // 5. File owner notifications
  const fileOwnerUrl = getFileOwnerUrl(notification);
  if (fileOwnerUrl) {
    return fileOwnerUrl;
  }

  // 6. Status note notifications
  const statusNoteUrl = getStatusNoteUrl(notification);
  if (statusNoteUrl) {
    return statusNoteUrl;
  }

  // 7. Deep links from backend
  const deepLinkUrl = getDeepLinkUrl(notification);
  if (deepLinkUrl) {
    return deepLinkUrl;
  }

  // 8. Legacy single certification notifications
  const legacyUrl = getLegacyCertificationUrl(notification);
  if (legacyUrl) {
    return legacyUrl;
  }

  // 9. Fallback to notification.url if available (normalize to current environment)
  if (notification.url) {
    const normalized = toCurrentEnvironmentUrl(notification.url);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};
